import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Mic, Square } from "lucide-react";

import { transcribeAudio, type TranscribeResponse } from "@/api/client";
import { Button } from "@/components/ui/button";

/**
 * One-button voice dictation: record → Whisper transcript → ``onTranscript``.
 *
 * Compact sibling of the Step-2 ``NaturalLanguageInput`` voice tab (same
 * MediaRecorder recipe, same ``/api/nl/transcribe`` endpoint) for places
 * where the transcript lands in an editable textarea the user already sees —
 * so no separate preview step is needed; the textarea IS the preview.
 */
export function VoiceDictationButton({
  onTranscript,
  disabled,
}: {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setUnsupported(true);
    }
  }, []);

  const transcribeMutation = useMutation({
    mutationFn: (blob: Blob) => transcribeAudio(blob),
    onSuccess: (data: TranscribeResponse) => {
      if (data.text?.trim()) onTranscript(data.text.trim());
    },
  });

  const startRecording = async () => {
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 1024) transcribeMutation.mutate(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      console.error("MediaRecorder failed", err);
      setUnsupported(true);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  if (unsupported) return null;

  return (
    <span className="inline-flex items-center gap-2">
      {!recording ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || transcribeMutation.isPending}
          onClick={startRecording}
        >
          {transcribeMutation.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Transkribiram…
            </>
          ) : (
            <>
              <Mic className="h-3.5 w-3.5" />
              Izdiktiraj
            </>
          )}
        </Button>
      ) : (
        <Button type="button" size="sm" onClick={stopRecording}>
          <Square className="h-3.5 w-3.5" />
          Zaustavi
        </Button>
      )}
      {recording && (
        <span className="flex items-center gap-1.5 text-[0.8rem] font-medium text-red-700">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" />
          Snimam…
        </span>
      )}
      {transcribeMutation.error instanceof Error && (
        <span className="text-[0.78rem] text-red-700">
          Transkripcija nije uspjela: {transcribeMutation.error.message}
        </span>
      )}
    </span>
  );
}
